import queryString from 'query-string';
import path from 'path';
import { Promise as BluebirdPromise } from 'bluebird';
import download from 'downloadjs';
import moment from 'moment';
import { ACTIONS } from '../Constants';
import { VerbFetch } from '../VerbFetch';
import { removeUrlSpaces } from '../utils/StringUtils';

const postToSignedUrl = (pathsContext, file, bucket, filename = '') => {
    const params = {
        method: 'POST',
        body: {
            name: filename || file.name,
            type: file.type,
            bucket
        }
    };
    return VerbFetch(`${pathsContext.mediaLibraryUrl}/upload`, params).then((result) => {
        const { uploadURL } = result;
        return fetch(uploadURL, {
            method: 'PUT',
            body: new Blob([file.file], { type: file.type })
        });
    });
};

export const getFileWithSignedUrl = (pathsContext, key, downloadFile = true) => {
    const fileServiceBaseUrl =
        (pathsContext && pathsContext.mediaLibraryUrl) || (pathsContext && pathsContext.serverUrl);

    if (!fileServiceBaseUrl || !key) {
        console.warn('getFileWithSignedUrl called with invalid arguments', {
            hasPathsContext: Boolean(pathsContext),
            hasMediaLibraryUrl: Boolean(pathsContext && pathsContext.mediaLibraryUrl),
            hasServerUrl: Boolean(pathsContext && pathsContext.serverUrl),
            hasKey: Boolean(key)
        });
        return BluebirdPromise.resolve(null);
    }

    const url = `${fileServiceBaseUrl}/file/${removeUrlSpaces(key)}?returnLink=true&download=${downloadFile}`;
    return VerbFetch(url).then((returnedUrl) => {
        if (!downloadFile) {
            return returnedUrl;
        }
        return download(returnedUrl);
    });
};

export const fileUploadBinary = (pathsContext, fileInfo) => (dispatch) => {
    try {
        const params = {
            method: 'POST',
            body: fileInfo
        };
        const ext = path.extname(fileInfo.name);
        const nameNoExt = fileInfo.name.replace(ext, '');

        const signedInfo = {
            name: `${nameNoExt}_${fileInfo.parentId}_1${ext}`,
            type: fileInfo.type,
            file: fileInfo.file
        };
        return postToSignedUrl(pathsContext, signedInfo, 'files')
            .then(() => VerbFetch(`${pathsContext.serverUrl}/files`, params))
            .then((result) => {
                dispatch({
                    type: ACTIONS.UPLOAD_NEW_FILE,
                    payload: {
                        file: result
                    }
                });
                return result;
            })
            .catch((err) => {
                throw new Error(err);
            });
    } catch (err) {
        console.error(err);
        return BluebirdPromise.reject(err);
    }
};

export const getManyFiles = (params) => (dispatch) => {
    const {pathsContext, ...query} = params
    const url = `${pathsContext.filesUrl}/files?${queryString.stringify(query)}`;
    return VerbFetch(url)
        .then((result) => {
            dispatch({
                type: ACTIONS.GET_MANY_FILES,
                payload: {
                    files: result
                }
            });
            return result;
        })
        .catch((err) => {
            throw new Error(err);
        });
};

export const htmlFilePost = (pathsContext, file, bucket) => {
    const params = {
        method: 'POST',
        body: {
            name: file.name,
            type: file.type,
            bucket
        }
    };

    return VerbFetch(`${pathsContext.mediaLibraryUrl}/upload`, params)
        .then((result) => {
            const { uploadURL } = result;
            return fetch(uploadURL, {
                method: 'PUT',
                body: new Blob([file.file], { type: file.type })
            });
        })
        .then((data) => data)
        .catch((err) => {
            throw new Error(err);
        });
};

export const htmlMediaFilePost = (pathsContext, mediaUrl, fileName, bucket) => {
    const params = {
        method: 'POST',
        body: {
            name: fileName,
            type: 'image/jpg',
            bucket
        }
    };
    let uploadURL;
    const secureMediaUrl = mediaUrl.replace('http:', 'https:');
    return VerbFetch(`${pathsContext.mediaLibraryUrl}/upload`, params)
        .then((result) => {
            ({ uploadURL } = result);
            return fetch(secureMediaUrl);
        })
        .then((response) => response.blob())
        .then((mediaBlob) =>
            fetch(uploadURL, {
                method: 'PUT',
                body: new Blob([mediaBlob], { type: 'image/jpg' })
            })
        )
        .catch((err) => {
            throw new Error(err);
        });
};

export const filePost = ({
    file,
    fileName,
    bucket,
    isPublic,
    licenses,
    originType = 'MEDIA_LIBRARY',
    originId = 'MEDIA_LIBRARY',
    profileId,
    pathsContext
}) => {
    const uniqueFileName = `${profileId}_${moment.utc().format()}_${file.name.split('.')[0]}.${
        file.name.split('.')[1]
    }`.replace(' ', '_');
    return postToSignedUrl(pathsContext, file, bucket, uniqueFileName)
        .then(() => {
            const body = {
                originType,
                originId,
                Key: uniqueFileName,
                fileName,
                Bucket: pathsContext.mediaBucket,
                isPublic,
                licenses
            };
            return VerbFetch(`${pathsContext.adminServerUrl}/filesmetadata`, {
                method: 'POST',
                body
            });
        })
        .catch((err) => {
            throw new Error(err);
        });
};

export const copyMediaToDestinationBucket = (params) => {
    const { pathsContext, ...rest } = params;
    const url = `${pathsContext.adminServerUrl}/files/copy`;
    return VerbFetch(url, {
        method: 'POST',
        body: rest
    }).then((result) => console.log('Copy Media to Destination result: ', result));
};

export const downloadAndSendToS3 = (pathsContext, url) => {
    const fileName = url.split('/').pop();
    return fetch(url, {
        method: 'GET'
    })
        .then((downloadResult) => downloadResult.blob())
        .then((blob) =>
            VerbFetch(`${pathsContext.mediaLibraryUrl}/upload`, {
                method: 'POST',
                body: {
                    name: fileName,
                    type: blob.type,
                    bucket: 'media'
                }
            }).then((signedUrl) => {
                const { uploadURL } = signedUrl;
                return fetch(uploadURL, {
                    method: 'PUT',
                    body: blob
                });
            })
        )
        .then((result) => console.log('Download and Send to S3 result: ', result));
};

export const deleteFileByMetadataId = (params) => {
    const { fileMetadataId, pathsContext } = params;
    const url = `${pathsContext.adminServerUrl}/files/${fileMetadataId}`;
    return VerbFetch(url, { method: 'DELETE' }).then((deleteResult) => deleteResult);
};

export const clearS3Folder = (params) => {
    const { pathsContext, ...rest } = params;
    const url = `${pathsContext.adminServerUrl}/files/clears3folder`;
    return VerbFetch(url, {
        method: 'POST',
        body: rest
    });
};

export const getLearningAssetsHTMLExports = ({ learningAssetId, pathsContext }) =>
    VerbFetch(`${pathsContext.adminServerUrl}/exporthtml/${learningAssetId}`);
